import 'regenerator-runtime/runtime';
import { call, CallEffect, select, takeEvery } from 'redux-saga/effects';
import {
  DoRequestResponse,
  GlobalAction,
  GlobalActions,
  GlobalPut,
  HTTP_METHOD,
} from '@ps20-frontend/shop-integration-redux';
import { App } from '@ps20-frontend/frontend-models';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { ProfiSportRisk } from '@ps20/bff-models';
import { IAppState } from '../appState';

export const SPORT_DISCIPLINE_EVENTS = {
  SEARCH_REQUESTED: 'SPORT_DISCIPLINE_SEARCH_REQUESTED',
  SEARCH_SUCCESSFUL: 'SPORT_DISCIPLINE_SEARCH_SUCCESSFUL',
  SEARCH_FAILED: 'SPORT_DISCIPLINE_SEARCH_FAILED',
  DETAILS_REQUESTED: 'SPORT_DISCIPLINE_DETAILS_REQUESTED',
  DETAILS_SUCCESSFUL: 'SPORT_DISCIPLINE_DETAILS_SUCCESSFUL',
  DETAILS_FAILED: 'SPORT_DISCIPLINE_DETAILS_FAILED',
} as const;

export interface SportDiscipline {
  id?: number;
  name: string;
  riskLevel: ProfiSportRisk;
  percentage: number;
}

export interface SportDisciplineItemResponse {
  id: number;
  sportart: string;
  absBaustein: string;
  profizuschlag: number;
  pbsLfnr: number;
  textbausteinReferenz: string;
  risikozuschlag: number;
}

export interface SportDisciplineResponse {
  sonderrisikenProfisportList: SportDisciplineItemResponse[];
}

export interface SearchSportDisciplinePayload {
  angebotsId: string;
  searchPattern: string;
  modules: string[];
}

export interface GetSportDisciplineDetailsPayload {
  angebotsId: string;
  sportId: number;
}

export function* watchSportDisciplineRequests() {
  yield takeEvery(
    SPORT_DISCIPLINE_EVENTS.SEARCH_REQUESTED,
    safeSaga(searchSportDiscipline, App.UNF),
  );
  yield takeEvery(
    SPORT_DISCIPLINE_EVENTS.DETAILS_REQUESTED,
    safeSaga(getSportDisciplineDetails, App.UNF),
  );
  yield takeEvery(
    SPORT_DISCIPLINE_EVENTS.SEARCH_SUCCESSFUL,
    safeSaga(searchSuccessCallback, App.UNF),
  );
  yield takeEvery(
    SPORT_DISCIPLINE_EVENTS.DETAILS_SUCCESSFUL,
    safeSaga(detailsSuccessCallback, App.UNF),
  );
}

export function* searchSportDiscipline(
  action: GlobalAction<SearchSportDisciplinePayload>,
): Generator {
  const store: IAppState = yield select();
  const { angebotsId, searchPattern, modules } = action.payload;

  if (angebotsId !== store.angebotsId) {
    return;
  }

  const globalPut: GlobalPut = GlobalPut.getInstance();

  const modulesParam = modules.join(',');
  const url = `/privatschutz/api/profisport/search?pattern=${encodeURIComponent(searchPattern)}&module=${encodeURIComponent(modulesParam)}`;

  yield call(
    globalPut.put.bind(globalPut),
    new GlobalActions().doRequest({
      appId: App.UNF,
      angebotsId,
      url,
      method: HTTP_METHOD.GET,
      successEvent: SPORT_DISCIPLINE_EVENTS.SEARCH_SUCCESSFUL,
      errorEvent: SPORT_DISCIPLINE_EVENTS.SEARCH_FAILED,
      originEvent: action,
    }),
  );
}

export function* getSportDisciplineDetails(
  action: GlobalAction<GetSportDisciplineDetailsPayload>,
): Generator {
  const store: IAppState = yield select();
  const { angebotsId, sportId } = action.payload;

  if (angebotsId !== store.angebotsId) {
    return;
  }

  const globalPut: GlobalPut = GlobalPut.getInstance();
  const url = `/privatschutz/api/profisport/item/${sportId}`;

  yield call(
    globalPut.put.bind(globalPut),
    new GlobalActions().doRequest({
      appId: App.UNF,
      angebotsId,
      url,
      method: HTTP_METHOD.GET,
      successEvent: SPORT_DISCIPLINE_EVENTS.DETAILS_SUCCESSFUL,
      errorEvent: SPORT_DISCIPLINE_EVENTS.DETAILS_FAILED,
      originEvent: action,
    }),
  );
}

export function* searchSuccessCallback(
  successAction: DoRequestResponse<SportDisciplineResponse>,
): Generator<CallEffect<boolean>, void, boolean> {
  try {
    if (successAction.doRequestAction.payload.appId === App.UNF) {
      const response: SportDisciplineResponse = successAction.payload;
      const angebotsId: string = successAction.doRequestAction.payload.angebotsId;

      const eventName = `${SPORT_DISCIPLINE_EVENTS.SEARCH_SUCCESSFUL}_${angebotsId}`;
      const event = new CustomEvent(eventName, { detail: response });
      yield call([window, window.dispatchEvent], event);
    }
  } catch (exception) {
    console.error('[SportDisciplineSaga] Error processing search results:', exception);
  }
}

export function* detailsSuccessCallback(
  successAction: DoRequestResponse<SportDisciplineItemResponse>,
): Generator<CallEffect<boolean>, void, boolean> {
  try {
    if (successAction.doRequestAction.payload.appId === App.UNF) {
      const response: SportDisciplineItemResponse = successAction.payload;
      const angebotsId: string = successAction.doRequestAction.payload.angebotsId;

      const eventName = `${SPORT_DISCIPLINE_EVENTS.DETAILS_SUCCESSFUL}_${angebotsId}`;
      const event = new CustomEvent(eventName, { detail: response });
      yield call([window, window.dispatchEvent], event);
    }
  } catch (exception) {
    console.error('[SportDisciplineSaga] Error processing sport details:', exception);
  }
}
